import { FeedbackRequest, Answer, TimelineEvent } from '../types/feedback';

export const INDIA_TIME_ZONE = 'Asia/Kolkata';

const asUtcDate = (date: string | Date): Date => {
  if (date instanceof Date) return date;

  // SQL DATETIME2 values are stored as UTC but are serialized without an offset.
  // Treat offset-less API timestamps as UTC before displaying them in IST.
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(date);
  return new Date(hasOffset ? date : `${date}Z`);
};

export const formatDateInIST = (date: string | Date): string =>
  new Intl.DateTimeFormat('en-IN', {
    timeZone: INDIA_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(asUtcDate(date));

export const formatDateTimeInIST = (date: string | Date): string =>
  new Intl.DateTimeFormat('en-IN', {
    timeZone: INDIA_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(asUtcDate(date));

/**
 * Maps an API response answer object to the frontend Answer type
 */
export const mapApiAnswer = (a: any): Answer => ({
  questionId: a.questionId,
  question: a.question,
  displayOrder: a.displayOrder,
  answer: a.answer || '',
  managerFeedback: a.managerFeedback || ''
});

/**
 * Extracts the employee submission date from the activity timeline.
 */
export const getEmployeeSubmittedDate = (timeline?: TimelineEvent[]): string | undefined => {
  if (!timeline || timeline.length === 0) return undefined;
  const event = timeline.find(e => {
    const act = e.action?.toLowerCase() || '';
    return act.includes('employee submitted') || act.includes('self-assessment');
  });
  return event?.date;
};

/**
 * Extracts the manager completion date from the activity timeline.
 */
export const getManagerCompletedDate = (timeline?: TimelineEvent[]): string | undefined => {
  if (!timeline || timeline.length === 0) return undefined;
  const event = timeline.find(e => {
    const act = e.action?.toLowerCase() || '';
    return act.includes('manager completed') || act.includes('completed review');
  });
  return event?.date;
};

/**
 * Maps an API response timeline object to the frontend TimelineEvent type
 */
export const mapApiTimeline = (t: any): TimelineEvent => ({
  date: t.actionDate,
  action: t.action,
  user: t.userName
});

/**
 * Maps a detailed API response (with answers and timeline) to a full FeedbackRequest
 */
export const mapApiFeedbackRequestFull = (data: any): FeedbackRequest => ({
  id: data.id,
  employeeId: data.employeeId || '',
  employeeName: data.employeeName,
  employeeEmail: data.employeeEmail || '',
  connectName: data.connectName || undefined,
  managerEmployeeId: data.managerEmployeeId || '',
  managerName: data.managerName,
  managerEmail: data.managerEmail || '',
  year: data.year,
  quarter: data.quarter,
  requestDate: new Date(data.requestDate).toISOString().split('T')[0],
  createdAt: data.createdAt,
  updatedAt: data.updatedAt,
  status: data.status as FeedbackRequest['status'],
  selfRating: data.selfRating || undefined,
  managerRating: data.managerRating || undefined,
  overallComments: data.overallComments || '',
  answers: data.answers?.map(mapApiAnswer) || [],
  questions: data.questions?.map((q: any) => ({
    id: q.id,
    question: q.question,
    questionText: q.question,
    placeholder: q.placeholder || '',
    displayOrder: q.displayOrder
  })) || undefined,
  timeline: data.timeline?.map(mapApiTimeline) || [],
  pip: data.pip ? {
    id: data.pip.id,
    feedbackRequestId: data.pip.feedbackRequestId,
    isOnPip: data.pip.isOnPip,
    pipStartDate: data.pip.pipStartDate,
    pipEndDate: data.pip.pipEndDate,
    pipReason: data.pip.pipReason,
    setByUserId: data.pip.setByUserId,
    setByUserName: data.pip.setByUserName,
    createdAt: data.pip.createdAt,
    updatedAt: data.pip.updatedAt
  } : undefined
});

/**
 * Maps an API response to a FeedbackRequest for list views (without full answers/timeline)
 */
export const mapApiFeedbackRequestList = (req: any): FeedbackRequest => ({
  id: req.id,
  employeeId: req.employeeId || '',
  employeeName: req.employeeName,
  employeeEmail: req.employeeEmail || '',
  connectName: req.connectName || undefined,
  managerEmployeeId: req.managerEmployeeId || '',
  managerName: req.managerName,
  managerEmail: req.managerEmail || '',
  year: req.year,
  quarter: req.quarter,
  requestDate: new Date(req.requestDate).toISOString().split('T')[0],
  createdAt: req.createdAt,
  updatedAt: req.updatedAt,
  status: req.status as FeedbackRequest['status'],
  canUpdateCv: req.canUpdateCv === true,
  selfRating: req.selfRating || undefined,
  managerRating: req.managerRating || undefined,
  overallComments: req.overallComments || '',
  pip: req.pip ? {
    id: req.pip.id,
    feedbackRequestId: req.pip.feedbackRequestId,
    isOnPip: req.pip.isOnPip,
    pipStartDate: req.pip.pipStartDate,
    pipEndDate: req.pip.pipEndDate,
    pipReason: req.pip.pipReason,
    setByUserId: req.pip.setByUserId,
    setByUserName: req.pip.setByUserName,
    createdAt: req.pip.createdAt,
    updatedAt: req.pip.updatedAt
  } : undefined,
  answers: [],
  timeline: []
});

/**
 * Maps an array of API responses to FeedbackRequest list
 */
export const mapApiFeedbackRequestsToList = (data: any[]): FeedbackRequest[] =>
  data.map(mapApiFeedbackRequestList);

/**
 * Formats a date for short display (e.g., "Jan 15, 2026")
 */
export const formatShortDate = (date: string | Date): string => {
  return formatDateInIST(date);
};

/**
 * Formats a date/time for timeline display (e.g., "01/15/2026 02:30 PM")
 */
export const formatDateTimeLocale = (date: string | Date): string => {
  return formatDateTimeInIST(date);
};
